import React, { useState, useEffect } from 'react';
import { Search, Filter } from 'lucide-react';
import PublicNavbar from '../../components/public/navbar/PublicNavbar';
import PublicFooter from '../../components/public/footer/PublicFooter';
import EquipmentCard from '../../components/public/cards/EquipmentCard';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import SEO from '../../components/shared/SEO';
import { getAllEquipment } from '../../services/equipment';
import './Equipment.css';

const Equipment = () => {
  const [equipment, setEquipment] = useState([]);
  const [filteredEquipment, setFilteredEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('name');

  useEffect(() => {
    loadEquipment();
  }, []);

  useEffect(() => {
    filterAndSortEquipment();
  }, [searchTerm, filterType, sortBy, equipment]);

  const loadEquipment = async () => {
    setLoading(true);
    const result = await getAllEquipment();
    if (result.success) {
      setEquipment(result.data);
      setFilteredEquipment(result.data);
    }
    setLoading(false);
  };

  const filterAndSortEquipment = () => {
    let filtered = [...equipment];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.brand.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.type.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(item => item.type === filterType);
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'price-low':
          return parseFloat(a.price) - parseFloat(b.price);
        case 'price-high':
          return parseFloat(b.price) - parseFloat(a.price);
        default:
          return 0;
      }
    });

    setFilteredEquipment(filtered);
  };

  const equipmentTypes = [...new Set(equipment.map(item => item.type))];

  return (
    <div className="public-equipment-page">
      <SEO
        title="Equipment Rentals"
        description="Rent professional film equipment in Chicago. Cameras, lenses, lighting, audio gear, and grip equipment for your production."
        keywords="camera rental Chicago, film equipment rental, lighting rental, audio equipment, grip equipment, production gear"
        url="/equipment"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Equipment', url: '/equipment' }
        ]}
        schema={{
          '@type': 'Service',
          'name': 'Film Equipment Rentals',
          'provider': {
            '@type': 'LocalBusiness',
            'name': 'IZZISHOWIN'
          },
          'serviceType': 'Film Equipment Rental',
          'areaServed': {
            '@type': 'City',
            'name': 'Chicago'
          },
          'description': 'Rent professional cameras, lenses, lighting, audio, and grip equipment for film and video productions in Chicago.'
        }}
      />
      <PublicNavbar />

      <div className="page-header">
        <div className="container">
          <h1 className="page-title">Professional Equipment</h1>
          <p className="page-subtitle">
            Rent top-quality production equipment for your project
          </p>
        </div>
      </div>

      <div className="page-content">
        <div className="container">
          {/* Filters */}
          <div className="filters-bar">
            <div className="search-box">
              <Search size={20} />
              <input
                type="text"
                placeholder="Search equipment..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            <div className="sort-box">
              <Filter size={20} />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="sort-select"
              >
                <option value="all">All Types</option>
                {equipmentTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="sort-box">
              <Filter size={20} />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="sort-select"
              >
                <option value="name">Sort by Name</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
              </select>
            </div>
          </div>

          {/* Results */}
          {loading ? (
            <LoadingSpinner />
          ) : filteredEquipment.length === 0 ? (
            <div className="empty-state">
              <h3 className="empty-title">No equipment found</h3>
              <p className="empty-text">
                {searchTerm || filterType !== 'all'
                  ? 'Try adjusting your search criteria'
                  : 'Check back soon for new equipment'}
              </p>
            </div>
          ) : (
            <>
              <div className="results-count">
                Showing {filteredEquipment.length} item{filteredEquipment.length !== 1 ? 's' : ''}
              </div>
              <div className="equipment-grid">
                {filteredEquipment.map((item) => (
                  <EquipmentCard key={item.id} equipment={item} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <PublicFooter />
    </div>
  );
};

export default Equipment;